import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@antimatterhq/ui/components/card';
import Tooltip from '@antimatterhq/ui/components/tooltip';
import { DatasourceStatus } from '../dataSourceCardAndTable/datasourceStatus/DatasourceStatus';
import { DataSourceEnabledBadge } from '../dataSourceCardAndTable/dataSourceEnabledBadge/DataSourceEnabledBadge';
import {
  DataSourceCardMetrics,
  DataSourceCardMetricsSkeleton,
} from '@/components/dataSources/dataSourceCard/dataSourceCardMetrics/DataSourceCardMetrics';
import { cn } from '@/lib/utils';
import { DatasourceSummary } from '@/types/api';
import { SuspenseErrorBoundary } from '@/components/errorBoundary/SuspenseErrorBoundary';
import { DataSourceMenu } from '@/components/dataSources/dataSourceCardAndTable/dataSourceMenu/DataSourceMenu';
import {
  DataSourceUpgrade,
  useDataSourceHasUpgrade,
} from '@/components/dataSources/dataSourceCardAndTable/dataSourceUpgrade/DataSourceUpgrade';

interface DatasourceCardProps {
  datasourceSummary: DatasourceSummary;
  'data-testid'?: string;
  isLoadingMetricsAndAlerts: boolean;
}

export const DatasourceCard = (props: DatasourceCardProps) => {
  const {
    datasourceSummary,
    'data-testid': testId,
    isLoadingMetricsAndAlerts,
  } = props;

  const showUpgradeButton = useDataSourceHasUpgrade(datasourceSummary);

  return (
    <>
      <Card
        className={'flex min-h-60 w-full flex-col px-4 py-4'}
        data-testid={testId}
      >
        <CardHeader className="space-y-1 p-0">
          <div className="flex justify-between">
            <div className="w-[calc(100%-80px)] flex-1">
              <Tooltip side="top" content={datasourceSummary.name}>
                <Text
                  size="lg"
                  semiBold
                  className="line-clamp-1 w-full overflow-hidden text-ellipsis"
                  data-testid={`${testId}-title`}
                >
                  {datasourceSummary.name}
                </Text>
              </Tooltip>
            </div>
            <div className="w-[80px] text-right">
              <DataSourceEnabledBadge
                enabled={datasourceSummary.enabled}
                data-testid={`${testId}-enabled-badge`}
              />
            </div>
          </div>
          <div className="flex">
            <div className="flex-1">
              <Text
                size="sm"
                color="secondary"
                className="-mt-1 line-clamp-3 overflow-hidden text-ellipsis"
                data-testid={`${testId}-description`}
              >
                {datasourceSummary.description}
                {datasourceSummary.description &&
                  'author' in datasourceSummary &&
                  datasourceSummary.author && <br />}
                {'author' in datasourceSummary &&
                  datasourceSummary.author &&
                  `By ${datasourceSummary.author}`}
              </Text>
            </div>
            <div className="w-[80px] text-right">
              <DatasourceStatus
                summaryWithAlertsAndMetrics={datasourceSummary}
                isLoadingMetricsAndAlerts={isLoadingMetricsAndAlerts}
                data-testid={`${testId}-status`}
              />
            </div>
          </div>
        </CardHeader>

        <Spacer size="base" />

        <CardContent className={cn('mt-auto flex w-full items-center p-0')}>
          <img
            className="h-16 w-40 overflow-hidden rounded-md object-contain"
            src={datasourceSummary.iconURL ?? '/images/default_icon.png'}
            alt={datasourceSummary.name ?? ''}
            data-testid={`${testId}-icon`}
          />
        </CardContent>

        <Spacer size="base" />

        <CardFooter className={cn('px-0 py-0')}>
          <SuspenseErrorBoundary
            suspenseFallback={<DataSourceCardMetricsSkeleton />}
          >
            <DataSourceCardMetrics
              summaryWithAlertsAndMetrics={datasourceSummary}
              isLoadingMetricsAndAlerts={isLoadingMetricsAndAlerts}
              data-testid={`${testId}-metrics`}
            />
          </SuspenseErrorBoundary>
          {showUpgradeButton && (
            <DataSourceUpgrade
              summaryWithAlertsAndMetrics={datasourceSummary}
              iconOnly
              data-testid={`${testId}-upgrade-button`}
            />
          )}
          <div
            className={cn(
              !showUpgradeButton && 'ml-3',
              showUpgradeButton && 'ml-[2px]',
            )}
          >
            <DataSourceMenu
              dataSource={datasourceSummary}
              data-testid={`${testId}-menu`}
            />
          </div>
        </CardFooter>
      </Card>
    </>
  );
};

export default DatasourceCard;
